/**
 * Beautiful and Responsive Cookie Consent
 * https://wordpress.org/plugins/beautiful-and-responsive-cookie-consent/
 * https://beautiful-cookie-banner.com/
 *
 * Cookie name: cookieconsent_status (customizable)
 * Cookie values for simple mode: "allow", "deny", "dismiss"
 * For detailed mode: "savesettings", "allowall", "deny"
 * Category cookies: cookieconsent_status_{suffix} with values "allow" or "deny"
 * Common suffixes: tech, marketing, etc.
 *
 * Events:
 * - beautiful_cookie_consent_initialized: Pushed to dataLayer on page load
 * - beautiful_cookie_consent_updated: Pushed to dataLayer on consent change
 *
 * Note: This plugin has built-in PMW integration via pmw.consent.api.updateSelectively()
 * This module handles reading the cookie on page load for returning visitors.
 */
export const beautifulCookieConsent = {

	/**
	 * Get the Beautiful Cookie Consent consent from cookies.
	 *
	 * @returns {{statistics: boolean, marketing: boolean, preferences: boolean, necessary: boolean}|null}
	 */
	getConsent: () => {

		// Try the default cookie name first
		let consent = pmw.getCookie("cookieconsent_status")

		// If not found, try to detect by looking for cookies that match the pattern
		if (!consent) {
			consent = pmw.getCookieThatContainsRegex(/^cookieconsent_status/)
		}

		if (!consent) return null

		pmw.console.log("Beautiful Cookie Consent CMP consent detected")

		// Simple consent modes: info, opt-in, opt-out
		// Values: "allow", "deny", "dismiss"
		if (consent === "allow" || consent === "dismiss") {
			return {
				statistics : true,
				marketing  : true,
				preferences: true,
				necessary  : true,
			}
		}

		if (consent === "deny") {
			return {
				statistics : false,
				marketing  : false,
				preferences: false,
				necessary  : true,
			}
		}

		// Detailed consent mode (differentiated consent)
		// Values: "savesettings", "allowall", "detailed"
		if (consent === "savesettings" || consent === "detailed" || consent === "allowall") {

			// If "allowall", all categories are allowed
			if (consent === "allowall") {
				return {
					statistics : true,
					marketing  : true,
					preferences: true,
					necessary  : true,
				}
			}

			// For "savesettings" or "detailed", we need to check individual category cookies
			// The plugin uses suffixes like: tech, marketing, etc.
			// Cookie format: cookieconsent_status_{suffix} with values "allow" or "deny"

			return {
				statistics : getCategoryConsent(["statistics", "analytics"]),
				marketing  : getCategoryConsent(["marketing"]),
				preferences: getCategoryConsent(["preferences", "functional", "tech"]),
				necessary  : true,
			}
		}

		// Fallback: try to read category cookies anyway
		// This handles cases where the main cookie has an unexpected value
		const marketingConsent = getCategoryConsent(["marketing"])
		const statisticsConsent = getCategoryConsent(["statistics", "analytics"])
		const preferencesConsent = getCategoryConsent(["preferences", "functional", "tech"])

		// Only return if we found at least one category cookie
		if (marketingConsent !== null || statisticsConsent !== null || preferencesConsent !== null) {
			return {
				statistics : statisticsConsent ?? true,
				marketing  : marketingConsent ?? true,
				preferences: preferencesConsent ?? true,
				necessary  : true,
			}
		}

		return null
	},

	/**
	 * Load event listeners for consent changes.
	 *
	 * Note: The Beautiful Cookie Consent plugin already calls pmw.consent.api.updateSelectively()
	 * directly when the Pixel Manager integration is enabled in the plugin settings.
	 * This event listener is a fallback for when that integration is not configured.
	 */
	loadEventListeners: () => {

		// Listen for dataLayer events
		// The plugin pushes "beautiful_cookie_consent_updated" when consent changes
		if (typeof window.dataLayer !== "undefined" && Array.isArray(window.dataLayer)) {
			observeDataLayer()
		} else {
			// Wait for dataLayer to be available
			window.addEventListener("DOMContentLoaded", () => {
				if (typeof window.dataLayer !== "undefined" && Array.isArray(window.dataLayer)) {
					observeDataLayer()
				}
			})
		}
	},
}

/**
 * Get consent for a specific category by checking various possible cookie suffixes.
 *
 * @param {string[]} suffixes - Array of possible suffixes to check
 * @returns {boolean|null} - true if allowed, false if denied, null if not found
 */
const getCategoryConsent = (suffixes) => {
	for (const suffix of suffixes) {
		const value = pmw.getCookie(`cookieconsent_status_${suffix}`)
		if (value === "allow") return true
		if (value === "deny") return false
	}
	return null
}

/**
 * Observe the dataLayer for Beautiful Cookie Consent events.
 */
const observeDataLayer = () => {

	// Create a proxy to intercept pushes to dataLayer
	const originalPush = window.dataLayer.push.bind(window.dataLayer)

	window.dataLayer.push = function (...args) {
		const result = originalPush(...args)

		// Check each pushed item for the consent update event
		args.forEach((item) => {
			if (item && item.event === "beautiful_cookie_consent_updated") {
				handleConsentUpdate(item)
			}
		})

		return result
	}
}

/**
 * Handle consent update from dataLayer event.
 *
 * @param {Object} eventData - The dataLayer event object
 */
const handleConsentUpdate = (eventData) => {

	pmw.console.log("Beautiful Cookie Consent consent update detected via dataLayer")

	// The event contains:
	// - cookieconsent_status: "allow", "deny", "savesettings", "allowall", etc.
	// - cookieconsent_status_{suffix}: "allow" or "deny" for each category

	const mainStatus = eventData.cookieconsent_status

	// Simple modes
	if (mainStatus === "allow" || mainStatus === "dismiss") {
		pmw.consent.api.updateSelectively({
			statistics : true,
			marketing  : true,
			preferences: true,
			necessary  : true,
		})
		return
	}

	if (mainStatus === "deny") {
		pmw.consent.api.updateSelectively({
			statistics : false,
			marketing  : false,
			preferences: false,
			necessary  : true,
		})
		return
	}

	if (mainStatus === "allowall") {
		pmw.consent.api.updateSelectively({
			statistics : true,
			marketing  : true,
			preferences: true,
			necessary  : true,
		})
		return
	}

	// Detailed mode - read category values from event data
	if (mainStatus === "savesettings" || mainStatus === "detailed") {
		const data = {
			statistics : getConsentFromEventData(eventData, ["statistics", "analytics"]),
			marketing  : getConsentFromEventData(eventData, ["marketing"]),
			preferences: getConsentFromEventData(eventData, ["preferences", "functional", "tech"]),
			necessary  : true,
		}

		pmw.consent.api.updateSelectively(data)
	}
}

/**
 * Get consent value from event data for a category.
 *
 * @param {Object} eventData - The dataLayer event object
 * @param {string[]} suffixes - Array of possible suffixes to check
 * @returns {boolean} - true if allowed, false otherwise
 */
const getConsentFromEventData = (eventData, suffixes) => {
	for (const suffix of suffixes) {
		const key = `cookieconsent_status_${suffix}`
		if (eventData[key] === "allow") return true
		if (eventData[key] === "deny") return false
	}
	// Default to false if no category found
	return false
}
